// SPDX-License-Identifier: MIT
import { Link, Navigate, useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiErrorMessage, coursesApi, quizApi } from '../../api/client'
import { useAuthStore } from '../../store/authStore'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useState } from 'react'
import { CheckCircle, ChevronRight, ChevronLeft, PlayCircle, FileText, FlaskConical, Lock } from 'lucide-react'
import toast from 'react-hot-toast'
import QuizPanel from '../../components/quiz/QuizPanel'

export default function LessonViewerPage() {
  const { slug, lessonId } = useParams<{ slug: string; lessonId: string }>()
  const { accessToken, mustChangePassword, user } = useAuthStore()
  if (accessToken && mustChangePassword) return <Navigate to="/change-password" replace />
  if (!slug || !lessonId) return <p role="alert">Lesson not found.</p>
  return <LessonContent key={`${slug}/${lessonId}/${user?.id || 'anonymous'}`} slug={slug} lessonId={lessonId} />
}

function LessonContent({ slug, lessonId }: { slug: string; lessonId: string }) {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const qc = useQueryClient()
  const [showQuiz, setShowQuiz] = useState(false)
  const { data: lesson, isLoading, error, refetch } = useQuery({
    queryKey: ['lesson', slug, lessonId, user?.id || 'anonymous'],
    queryFn: () => coursesApi.getLesson(slug, lessonId).then(r => r.data),
  })
  const { data: course, error: courseError, refetch: refetchCourse } = useQuery({
    queryKey: ['course', slug, user?.id || 'anonymous'],
    queryFn: () => coursesApi.get(slug).then(r => r.data),
  })
  const { data: quiz } = useQuery({
    queryKey: ['quiz', slug, lessonId, user?.id],
    queryFn: () => quizApi.get(slug, lessonId).then(r => r.data),
    enabled: !!lesson?.has_quiz && !!user && (
      !!course?.is_enrolled || user.role === 'admin' || user.id === course?.instructor.id
    ),
  })
  const { data: progress } = useQuery({
    queryKey: ['course-progress', slug, user?.id],
    queryFn: () => coursesApi.getCourseProgress(slug).then(r => r.data),
    enabled: !!user && !!course?.is_enrolled,
  })
  const progressMut = useMutation({
    mutationFn: () => coursesApi.updateProgress(slug, lessonId, true),
    onSuccess: ({ data }) => {
      qc.setQueryData(['course-progress', slug, user?.id], data)
      qc.invalidateQueries({ queryKey: ['course', slug] })
      qc.invalidateQueries({ queryKey: ['lesson', slug, lessonId] })
      qc.invalidateQueries({ queryKey: ['my-certs'] })
      toast.success(data.certificate_available ? 'Course completed. Your certificate is ready.' : 'Lesson completed', { id: 'course-progress' })
    },
    onError: (failure: unknown) => toast.error(apiErrorMessage(failure, 'Unable to save your progress')),
  })
  const allLessons = course?.modules.flatMap(module => module.lessons) || []
  const canAssess = !!user && (
    !!course?.is_enrolled || user.role === 'admin' || user.id === course?.instructor.id
  )
  const completed = course?.modules.flatMap(module => module.lessons).find(item => item.id === lessonId)?.is_completed ?? lesson?.is_completed
  const currentIdx = allLessons.findIndex(item => item.id === lessonId)
  const prevLesson = currentIdx > 0 ? allLessons[currentIdx - 1] : null
  const nextLesson = currentIdx >= 0 && currentIdx < allLessons.length - 1 ? allLessons[currentIdx + 1] : null

  if (isLoading) return <div className="flex items-center justify-center h-64 text-brand-muted">Loading lesson...</div>
  if (error) return (
    <div className="max-w-4xl mx-auto px-4 py-12" role="alert">
      <p>{apiErrorMessage(error, 'Unable to load this lesson')}</p>
      <button className="btn-outline mt-4" onClick={() => refetch()}>Retry</button>
      {!user && <Link className="btn-primary ml-3" to="/login">Sign in</Link>}
    </div>
  )
  if (!lesson) return <p className="text-center py-20" role="alert">Lesson not found.</p>

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 grid md:grid-cols-4 gap-6">
      <aside className="md:col-span-1 order-2 md:order-1 min-w-0">
        <div className="sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto">
          <h2 className="font-bold text-sm mb-3 text-brand-muted uppercase tracking-wide">Course Content</h2>
          {courseError && <p role="alert">Unable to load the curriculum. <button onClick={() => refetchCourse()}>Retry</button></p>}
          <div className="space-y-2">
            {course?.modules.map(module => (
              <div key={module.id}>
                <p className="text-xs font-semibold text-brand-muted uppercase tracking-wide py-1.5 px-2">
                  {module.title}
                  {!!module.lessons.length && module.lessons.every(item => item.is_completed) && <span className="block text-brand-green">Chapter completed</span>}
                </p>
                {module.lessons.map(item => (
                  <button key={item.id} disabled={!item.is_accessible} title={item.lock_reason || undefined}
                    aria-current={item.id === lessonId ? 'page' : undefined}
                    onClick={() => navigate(`/learn/${slug}/lessons/${item.id}`)}
                    className={`sidebar-link w-full ${item.id === lessonId ? 'active' : ''}`}>
                    {item.is_completed ? <CheckCircle size={13} className="text-brand-green" aria-label="Completed" />
                      : !item.is_accessible ? <Lock size={13} aria-label="Locked" />
                      : item.lesson_type === 'video' ? <PlayCircle size={13} /> : <FileText size={13} />}
                    <span className="text-xs text-left">{item.title}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      </aside>
      <main className="md:col-span-3 order-1 md:order-2 min-w-0">
        <h1 className="text-2xl font-extrabold mb-4">{lesson.title}</h1>
        {progress && <p className="text-sm text-brand-muted mb-4" role="status">{progress.completed_lessons} of {progress.total_lessons} lessons completed ({progress.progress_percent}%).</p>}
        {lesson.is_preview && !user && <p className="card mb-6">Public teaching preview. Full course enrolment is coming soon; no account is needed to read this lesson.</p>}
        {lesson.lesson_type === 'video' && lesson.video_url && (
          <div className="aspect-video w-full mb-6 rounded-xl overflow-hidden bg-black">
            <iframe src={lesson.video_url} className="w-full h-full" allowFullScreen title={lesson.title} />
          </div>
        )}
        {lesson.content_md && (
          <div className="prose max-w-none mb-8 leading-relaxed [overflow-wrap:anywhere] prose-headings:text-slate-900 prose-code:text-slate-800 prose-pre:bg-slate-100 prose-pre:text-slate-800 prose-a:text-brand-blue">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{lesson.content_md}</ReactMarkdown>
          </div>
        )}
        {lesson.has_quiz && canAssess && !showQuiz && (
          <button className="btn-outline mb-6" onClick={() => setShowQuiz(true)}>
            <FlaskConical size={16} /> {quiz?.has_passed ? 'Review Knowledge Check' : 'Take Knowledge Check'}
          </button>
        )}
        {lesson.has_quiz && canAssess && showQuiz && <QuizPanel key={`${lessonId}/${quiz?.content_revision}`} slug={slug} lessonId={lessonId} />}
        {progressMut.isError && <p role="alert" className="text-red-700">{apiErrorMessage(progressMut.error, 'Unable to save your progress')}</p>}
        <div className="flex flex-wrap gap-3 items-center justify-between pt-6 border-t border-brand-border mt-8">
          <button className="btn-outline" disabled={!prevLesson?.is_accessible} onClick={() => prevLesson && navigate(`/learn/${slug}/lessons/${prevLesson.id}`)}>
            <ChevronLeft size={16} /> Previous
          </button>
          {user && course?.is_enrolled && (
            <button className="btn-ghost text-sm flex items-center gap-2 text-brand-green"
              disabled={completed || progressMut.isPending || (lesson.has_quiz && !quiz?.has_passed)}
              onClick={() => progressMut.mutate()}>
              <CheckCircle size={16} /> {completed ? 'Completed' : progressMut.isPending ? 'Saving...' : 'Mark complete'}
            </button>
          )}
          <button className="btn-primary" disabled={!nextLesson?.is_accessible} title={nextLesson?.lock_reason || undefined} onClick={() => nextLesson && navigate(`/learn/${slug}/lessons/${nextLesson.id}`)}>
            Next <ChevronRight size={16} />
          </button>
        </div>
        {lesson.has_quiz && course?.is_enrolled && !quiz?.has_passed && <p className="text-sm text-brand-muted mt-3">Pass the knowledge check to complete this chapter and unlock the next one.</p>}
        {nextLesson && !nextLesson.is_accessible && course?.is_enrolled && <p className="text-sm text-brand-muted mt-3">Complete this lesson before continuing. Chapter knowledge checks must be passed.</p>}
        {progress?.certificate_available && <p className="card mt-6 text-brand-green" role="status">Course completed. <Link to="/certificates" className="text-brand-blue underline">Download your certificate of completion</Link>. This is not an accredited diploma.</p>}
      </main>
    </div>
  )
}
